import classnames from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetStreakLeaderboards } from '@/query/queries/streak/useGetStreakLeaderboards';
import { getFlagURL, prettyFloatMoney } from '@/helpers/utils';
import { MyLink } from '@/components/MyLink';
import { APP_LINKS } from '@/helpers/links';
import IconStar from './assets/icon_star.svg';
import IconFlag from './assets/icon_flag.svg';
import IconUp from './assets/icon_up.svg';
import IconDown from './assets/icon_down.svg';
import IconEmpty from './assets/icon_empty.svg';
import r from '@/kit/RoundButton/button.module.scss';
import s from './index.module.scss';

export const LeaderBoards = ({ status }) => {
  const { t } = useTranslation('streak');
  const [byCountry, setByCountry] = useState(false);
  const { data } = useGetStreakLeaderboards({
    by_country: JSON.stringify(byCountry),
  });

  const hasStreak = !!status?.streak;

  const rankIcon = rank => {
    switch (rank) {
      case 'up':
        return <img src={IconUp} alt="up" width={8} height={8} />;
      case 'down':
        return <img src={IconDown} alt="down" width={8} height={8} />;
      default:
        return <img src={IconEmpty} alt="unchanged" width={8} height={8} />;
    }
  };

  return (
    <div className={s.wrapper}>
      <div className={s.header}>
        <h2 className="f-h2 f-500">{t('leaderboards.title')}</h2>
        {hasStreak && (
          <div className={s.switch}>
            <button
              className={classnames(!byCountry && 'active', 'f-small-accent')}
              type="button"
              onClick={() => setByCountry(false)}
            >
              <img src={IconStar} alt="star" width={20} height={20} />
              <span>{t('leaderboards.all_investors')}</span>
            </button>
            <button
              className={classnames(byCountry && 'active', 'f-small-accent')}
              type="button"
              onClick={() => setByCountry(true)}
            >
              <img src={IconFlag} alt="flag" width={20} height={20} />
              <span>{t('leaderboards.your_country')}</span>
            </button>
          </div>
        )}
      </div>

      {hasStreak ? (
        <div className={s.table_wrapper}>
          <div className={s.table_inner}>
            <div className={s.table}>
              <div className={s.table__thead}>
                <div className={s.table__thead_col}>
                  {t('leaderboards.rank')}
                </div>
                <div className={s.table__thead_col}>
                  {t('leaderboards.investor')}
                </div>
                <div className={s.table__thead_col}>
                  {t('leaderboards.current_streak')}
                </div>
                <div className={s.table__thead_col}>
                  {t('leaderboards.total_rounds')}
                </div>
                <div className={s.table__thead_col}>
                  {t('leaderboards.total_invested')}
                </div>
              </div>

              <div className={s.table__tbody}>
                {data?.map((item, index) => (
                  <div
                    key={index}
                    className={classnames(
                      s.table__tbody_row,
                      item.self && 'active',
                    )}
                  >
                    <div className={s.table__tbody_col}>
                      <div className={s.rank}>
                        {rankIcon(item.rank_state)}
                        <span className="f-small-accent">#{item.rank}</span>
                      </div>
                    </div>
                    <div className={classnames(s.table__tbody_col, s.row)}>
                      <div className={s.avatar}>
                        <span className="f-tiny-accent">{item.avatar}</span>
                        <div className={s.country}>
                          <img
                            src={getFlagURL(item.country_code)}
                            alt={item.country_code}
                            width={14}
                            height={14}
                          />
                        </div>
                      </div>
                      {item.self && (
                        <div className={classnames(s.you, 'f-tiny-accent')}>
                          {t('leaderboards.you')}
                        </div>
                      )}
                    </div>
                    <div className={s.table__tbody_col}>
                      <span className="f-small-accent">
                        {t('leaderboards.rounds', {
                          rounds: item.current_streak,
                        })}
                      </span>
                    </div>
                    <div className={s.table__tbody_col}>
                      <span className="f-small-regular">
                        {t('leaderboards.rounds', {
                          rounds: item.total_rounds,
                        })}
                      </span>
                    </div>
                    <div className={s.table__tbody_col}>
                      <span className="f-small-regular">
                        {prettyFloatMoney(item.total_invested, {
                          decimalPlaces: 2,
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={s.empty}>
          <div className={s.empty_content}>
            <div className={s.empty_star}>
              <img src={IconStar} alt="star" width={32} height={32} />
            </div>
            <div className={classnames(s.empty_title, 'f-article-accent')}>
              {t('leaderboards.journey_not_started')}
            </div>
            <div className={classnames(s.empty_desc, 'f-small-regular')}>
              {t('leaderboards.make_first_investment')}
            </div>
            <MyLink
              href={APP_LINKS.investing}
              className={classnames(
                s.empty_link,
                r.black_small,
                'f-body-accent',
              )}
            >
              {t('leaderboards.start_investing')}
            </MyLink>
          </div>
        </div>
      )}
    </div>
  );
};
